/**
 * Parameter Classification System for Care360 Simulation Sandbox
 *
 * Categorizes all 481 parameters from parameters.md into appropriate types
 * for simulation UI controls and clinical relationship modeling.
 * 
 * Classification Types:
 * - CONTINUOUS: Numeric parameters with ranges (suitable for sliders)
 * - CATEGORICAL: Discrete options (dropdowns/radio buttons)
 * - BINARY: Yes/No or Present/Absent (checkboxes/toggles)
 * - DERIVED: Calculated from other parameters (read-only displays)
 * - ORDINAL: Ordered categories (rating scales)
 */

/**
 * Parameter type definitions
 */
export const PARAMETER_TYPES = {
  CONTINUOUS: 'continuous',
  CATEGORICAL: 'categorical', 
  BINARY: 'binary',
  DERIVED: 'derived',
  ORDINAL: 'ordinal'
};

/**
 * Clinical significance levels for prioritization
 */
export const CLINICAL_SIGNIFICANCE = {
  CRITICAL: 'critical',     // Life-threatening parameters (BP, glucose, etc.)
  HIGH: 'high',            // Major health indicators (lipids, HbA1c, etc.)
  MODERATE: 'moderate',    // Important but not critical (vitamins, etc.)
  LOW: 'low'              // Supplementary information
};

/**
 * Evidence strength levels for relationship prioritization
 */
export const EVIDENCE_LEVELS = {
  LEVEL_1: 1,  // Meta-analyses, systematic reviews of RCTs
  LEVEL_2: 2,  // Individual RCTs
  LEVEL_3: 3,  // Cohort studies, case-control studies
  LEVEL_4: 4   // Expert opinion, case series
};

/**
 * Patient engagement value for lifestyle modification potential
 */
export const ENGAGEMENT_VALUE = {
  HIGH: 'high',      // Directly modifiable by patient (exercise, diet)
  MODERATE: 'moderate', // Partially modifiable (sleep, stress)
  LOW: 'low'         // Not directly modifiable (genetics, age)
};

/**
 * Comprehensive parameter classification database
 */
export const PARAMETER_CLASSIFICATION = {
  
  // === VITALS (15 parameters) ===
  'vitals_temp_c': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 35.0, max: 42.0, step: 0.1 },
    unit: '°C',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.CRITICAL,
    engagementValue: ENGAGEMENT_VALUE.LOW,
    normalRange: { min: 36.1, max: 37.2 },
    criticalLimits: { min: 35.0, max: 40.0 }
  },
  
  'vitals_sbp': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 70, max: 250, step: 1 }, // Static fallback range
    unit: 'mmHg',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.CRITICAL,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { min: 90, max: 140 },
    criticalLimits: { min: 80, max: 200 },
    relationships: ['lifestyle_movement_moderate_activity_days', 'lifestyle_nutrition_sodium_intake'],
    // Dynamic range configuration for HealthSnapshotV2 integration
    supportsDynamicRange: true,
    dynamicRangeConfig: {
      safetyBounds: { min: 50, max: 300 }, // Physiological safety limits
      targetSource: 'dynamic_formula', // Uses calculateDynamicTarget()
      targetKey: 'vitals_sbp', // Key for target formula lookup
      sdSource: 'scoring_config', // Standard deviation from scoring configuration
      maxZThreshold: 6.0 // Same as HealthSnapshotV2
    }
  },
  
  'vitals_dbp': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 40, max: 150, step: 1 },
    unit: 'mmHg',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.CRITICAL,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { min: 60, max: 90 },
    criticalLimits: { min: 50, max: 120 },
    relationships: ['lifestyle_movement_moderate_activity_days', 'lifestyle_nutrition_sodium_intake']
  },
  
  'vitals_rhr': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 40, max: 150, step: 1 },
    unit: 'bpm',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { min: 60, max: 100 },
    criticalLimits: { min: 45, max: 120 },
    relationships: ['lifestyle_movement_moderate_activity_days', 'lifestyle_recovery_sleep_quality']
  },
  
  'vitals_weight': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 30, max: 300, step: 0.1 },
    unit: 'kg',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { min: 50, max: 100 }, // Highly variable by individual
    relationships: ['lifestyle_nutrition_protein_intake', 'lifestyle_movement_moderate_activity_days']
  },
  
  'vitals_height': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 100, max: 250, step: 0.1 },
    unit: 'cm',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.MODERATE,
    engagementValue: ENGAGEMENT_VALUE.LOW, // Not modifiable in adults
    normalRange: { min: 150, max: 200 }
  },
  
  'vitals_bmi': {
    type: PARAMETER_TYPES.DERIVED,
    formula: 'weight_kg / (height_m^2)',
    dependencies: ['vitals_weight', 'vitals_height'],
    unit: 'kg/m²',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    normalRange: { min: 18.5, max: 24.9 },
    categories: {
      'underweight': { max: 18.5 },
      'normal': { min: 18.5, max: 24.9 },
      'overweight': { min: 25.0, max: 29.9 },
      'obese': { min: 30.0 }
    }
  },
  
  'vitals_map': {
    type: PARAMETER_TYPES.DERIVED,
    formula: 'dbp + (sbp - dbp) / 3',
    dependencies: ['vitals_sbp', 'vitals_dbp'],
    unit: 'mmHg',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    normalRange: { min: 70, max: 100 }
  },
  
  'vitals_pp': {
    type: PARAMETER_TYPES.DERIVED,
    formula: 'sbp - dbp',
    dependencies: ['vitals_sbp', 'vitals_dbp'],
    unit: 'mmHg',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.MODERATE,
    normalRange: { min: 30, max: 50 }
  },
  
  // === LABS - LIPID PANEL (7 parameters) ===
  'labs_lipid_ldl': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 30, max: 400, step: 1 },
    unit: 'mg/dL',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.CRITICAL,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { min: 70, max: 100 },
    optimalRange: { max: 70 },
    relationships: ['lifestyle_nutrition_protein_intake', 'lifestyle_movement_moderate_activity_days']
  },
  
  'labs_lipid_hdl': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 20, max: 120, step: 1 },
    unit: 'mg/dL',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { min: 40, max: 60 },
    optimalRange: { min: 60 },
    relationships: ['lifestyle_movement_moderate_activity_days', 'lifestyle_nutrition_protein_intake']
  },
  
  'labs_lipid_trig': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 30, max: 1000, step: 5 },
    unit: 'mg/dL',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { max: 150 },
    relationships: ['lifestyle_nutrition_protein_intake', 'lifestyle_movement_moderate_activity_days']
  },
  
  // === LABS - GLUCOSE (2 parameters) ===
  'labs_glucose_glucose': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 50, max: 400, step: 1 },
    unit: 'mg/dL',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.CRITICAL,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { min: 70, max: 100 },
    criticalLimits: { min: 60, max: 300 },
    relationships: ['lifestyle_nutrition_protein_intake', 'lifestyle_movement_moderate_activity_days', 'lifestyle_recovery_sleep_quality']
  },
  
  'labs_glucose_hba1c': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 4.0, max: 15.0, step: 0.1 },
    unit: '%',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.CRITICAL,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { max: 5.7 },
    diabeticRange: { min: 6.5 },
    relationships: ['lifestyle_nutrition_protein_intake', 'lifestyle_movement_moderate_activity_days']
  },
  
  // === LIFESTYLE - MOVEMENT (14 parameters) ===
  'lifestyle_movement_moderate_activity_days': {
    type: PARAMETER_TYPES.ORDINAL,
    range: { min: 0, max: 7, step: 1 },
    unit: 'days/week',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    evidenceLevel: EVIDENCE_LEVELS.LEVEL_1,
    categories: {
      'sedentary': { max: 1 },
      'low': { min: 2, max: 3 },
      'moderate': { min: 4, max: 5 },
      'high': { min: 6, max: 7 }
    },
    affects: ['vitals_sbp', 'vitals_dbp', 'vitals_rhr', 'labs_glucose_glucose', 'labs_lipid_hdl', 'labs_lipid_trig']
  },
  
  'lifestyle_movement_vigorous_activity_days': {
    type: PARAMETER_TYPES.ORDINAL,
    range: { min: 0, max: 7, step: 1 },
    unit: 'days/week',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    evidenceLevel: EVIDENCE_LEVELS.LEVEL_1
  },
  
  // === LIFESTYLE - NUTRITION (29 parameters) ===
  'lifestyle_nutrition_protein_intake': {
    type: PARAMETER_TYPES.ORDINAL,
    range: { min: 1, max: 5, step: 0.5 },
    unit: 'quality score',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    evidenceLevel: EVIDENCE_LEVELS.LEVEL_1,
    categories: {
      'poor': { max: 2 },
      'fair': { min: 2.5, max: 3 },
      'good': { min: 3.5, max: 4 },
      'excellent': { min: 4.5 }
    },
    affects: ['labs_lipid_ldl', 'labs_lipid_hdl', 'labs_glucose_glucose', 'vitals_sbp']
  },
  
  // === LIFESTYLE - RECOVERY (20 parameters) ===
  'lifestyle_recovery_sleep_quality': {
    type: PARAMETER_TYPES.ORDINAL,
    range: { min: 1, max: 5, step: 0.5 },
    unit: 'quality score',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    evidenceLevel: EVIDENCE_LEVELS.LEVEL_1,
    categories: {
      'poor': { max: 2 },
      'fair': { min: 2.5, max: 3 },
      'good': { min: 3.5, max: 4 },
      'excellent': { min: 4.5 }
    },
    affects: ['labs_glucose_glucose', 'vitals_sbp', 'vitals_rhr']
  },
  
  'lifestyle_recovery_sleep_duration': {
    type: PARAMETER_TYPES.CONTINUOUS,
    range: { min: 3, max: 12, step: 0.5 },
    unit: 'hours',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    normalRange: { min: 7, max: 9 }
  },

  // === LIFESTYLE - EMOTION (10 parameters) ===
  'lifestyle_emotion_stress_level': {
    type: PARAMETER_TYPES.ORDINAL,
    range: { min: 1, max: 5, step: 0.5 },
    unit: 'stress level',
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.HIGH,
    evidenceLevel: EVIDENCE_LEVELS.LEVEL_1,
    categories: {
      'minimal': { max: 1.5 },
      'low': { min: 2, max: 2.5 },
      'moderate': { min: 3, max: 3.5 },
      'high': { min: 4, max: 4.5 },
      'severe': { min: 5 }
    },
    affects: ['vitals_sbp', 'vitals_dbp', 'vitals_rhr', 'labs_glucose_glucose'],
    description: 'Self-reported stress level on a 1-5 scale where 1=minimal stress and 5=severe stress'
  },
  
  // === PHYSICAL EXAM - BINARY EXAMPLES ===
  'pe_integumentary_alopecia': {
    type: PARAMETER_TYPES.BINARY,
    options: ['Present', 'Absent'],
    clinicalSignificance: CLINICAL_SIGNIFICANCE.LOW,
    engagementValue: ENGAGEMENT_VALUE.LOW
  },
  
  'pe_cardio_heart_rhythm': {
    type: PARAMETER_TYPES.CATEGORICAL,
    options: ['Regular', 'Irregular', 'Tachycardic', 'Bradycardic'],
    clinicalSignificance: CLINICAL_SIGNIFICANCE.HIGH,
    engagementValue: ENGAGEMENT_VALUE.LOW
  },
  
  // === IMAGING - CATEGORICAL EXAMPLES ===
  'imaging_liver_us_echogenicity_steatosis': {
    type: PARAMETER_TYPES.CATEGORICAL,
    options: ['Normal', 'Mild steatosis', 'Moderate steatosis', 'Severe steatosis'],
    clinicalSignificance: CLINICAL_SIGNIFICANCE.MODERATE,
    engagementValue: ENGAGEMENT_VALUE.MODERATE
  }
};

/**
 * Get parameters by type for UI rendering
 */
export const getParametersByType = (type) => {
  return Object.entries(PARAMETER_CLASSIFICATION)
    .filter(([key, config]) => config.type === type)
    .map(([key, config]) => ({ key, ...config }));
};

/**
 * Get high-priority parameters for initial implementation
 */
export const getHighPriorityParameters = () => {
  return Object.entries(PARAMETER_CLASSIFICATION)
    .filter(([key, config]) => 
      config.clinicalSignificance === CLINICAL_SIGNIFICANCE.CRITICAL ||
      (config.clinicalSignificance === CLINICAL_SIGNIFICANCE.HIGH && 
       config.engagementValue === ENGAGEMENT_VALUE.HIGH)
    )
    .map(([key, config]) => ({ key, ...config }));
};

/**
 * Get parameters with established relationships
 * MODIFIED: Excludes lifestyle parameters to focus on health systems only
 */
export const getParametersWithRelationships = () => {
  return Object.entries(PARAMETER_CLASSIFICATION)
    .filter(([key, config]) => {
      // Exclude lifestyle parameters
      if (key.startsWith('lifestyle_')) {
        return false;
      }

      return config.affects || config.relationships;
    })
    .map(([key, config]) => ({ key, ...config }));
};

/**
 * Get parameters suitable for sliders (continuous + ordinal)
 * MODIFIED: Excludes lifestyle parameters to focus on health systems only
 */
export const getSliderParameters = () => {
  return Object.entries(PARAMETER_CLASSIFICATION)
    .filter(([key, config]) => {
      // Exclude lifestyle parameters (lifestyle_movement_, lifestyle_nutrition_, etc.)
      if (key.startsWith('lifestyle_')) {
        return false;
      }

      return config.type === PARAMETER_TYPES.CONTINUOUS ||
             config.type === PARAMETER_TYPES.ORDINAL;
    })
    .map(([key, config]) => ({ key, ...config }));
};

export default {
  PARAMETER_TYPES,
  CLINICAL_SIGNIFICANCE,
  EVIDENCE_LEVELS,
  ENGAGEMENT_VALUE,
  PARAMETER_CLASSIFICATION,
  getParametersByType,
  getHighPriorityParameters,
  getParametersWithRelationships,
  getSliderParameters
};
